import {
  BrandThemeManager,
  Fonts,
  InteractiveContentV2
} from "@paperflite-marketing/cleverstory-commons";
import { fetchCustomFields } from "actions/fields/fields";
import { get } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import PageWithContext from "./PageWithContext";
import { useGating } from "./hooks/useGating";

function MultipageWrapper({
  storyboard,
  pagesWithFrames,
  onChange,
  activePage,
  activePageIndex,
  pagesById,
}) {
  const [windowHeight, setWindowHeight] = useState("100vh");
  const page = useSelector((state) => get(state, `pages.page[${activePage}].item`));
  const pageLoaded = get(page, "id") != null;
  const desktop = useMediaQuery({ query: "(min-width: 1024px)" });
  const fonts = useSelector(() => get(page, `content.fonts`) || []);
  const gatingConfigurations = useSelector((state) => get(state, `pages.page.${activePage}.item.gatings`, []));
  const domain = useSelector(state => get(state, 'workspaces.workspace.item.defaultDomain') || "");
  const domainUrl = get(storyboard, 'domain') || domain;
  const anchorsRef = useRef(null);
  const dispatch = useDispatch();
  const { gatingRef, handleResetGating } = useGating(gatingConfigurations, pageLoaded);

  Fonts.useFont(fonts);

  const handleWindowResize = () => {
    const height = window.innerHeight;
    setWindowHeight(`${height}px`);
  };

  {/* Scrolls to the anchor widget once the page is loaded and the element is in the DOM. */}
  useEffect(() => {
    const anchorData = anchorsRef.current;
    if (!anchorData || !page) return;
    const { handleScrollToWidget, anchorScrollData } = anchorData;
    
    const observer = new MutationObserver(() => {
      const widgetElement = document.getElementById(anchorScrollData.widget);
      if (widgetElement && page.id === anchorScrollData.pageId) {
        handleScrollToWidget({ value: anchorScrollData.widget, pageIndex: anchorScrollData.pageIndex });
        anchorsRef.current = null;
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [page]);

  useEffect(() => {
    window.addEventListener("resize", handleWindowResize);
    handleWindowResize();
    if (storyboard) {
      dispatch(fetchCustomFields(get(storyboard, "id")));
    }
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            :root {
              --window-height: ${windowHeight};
            }
            .ic_versionTwo:has(>.loading) {
              height: 100%;
            }
          `,
        }}
      />
      <BrandThemeManager
        className={".cs_text_widget"}
        themes={get(storyboard, "themes")}
      />
      <InteractiveContentV2
        initialPage={Math.max(activePageIndex, 0)}
        downloadUrl={null}
        appearance={get(storyboard, "appearance")}
        onChange={onChange}
        onDownload={() => null}
        versionTwo={true}
        anchorsRef={anchorsRef}
      >
        {pagesWithFrames.map((frames) => {
          const icon = pagesById[frames[0].pageId]?.icon
          return (
            <PageWithContext
              key={frames[0].pageId}
              frames={frames}
              name={pagesById[frames[0].pageId]?.name}
              storyboard={storyboard}
              pagesById={pagesById}
              domainUrl={domainUrl}
              desktop={desktop}
              gatingRef={gatingRef}
              gatingConfigurations={gatingConfigurations}
              handleResetGating={handleResetGating}
              icon={icon}
            />
          );
        })}
      </InteractiveContentV2>
    </>
  );
}

export { MultipageWrapper };
